import { forwardRef } from 'react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import techTennisLogo from '../assets/techtennis_logo_wimbledon.png';

export interface ReportData {
  professorName: string;
  professorLogo: string | null;
  student: any;
  evolutionLogs: any[];
  finances: any[];
  allFinances?: any[];
  sections: {
    cadastro: boolean;
    anamnese: boolean;
    desempenho: boolean;
    financeiro: boolean;
  };
}

export const ExecutiveReportTemplate = forwardRef<HTMLDivElement, any>(({ student, professorName, professorLogo, sections, evolutionLogs, finances, allFinances }, ref) => {
  if (!student) return null;

  // Calculate averages for radar chart
  const latest = evolutionLogs.length > 0 ? evolutionLogs[0] : {};
  const radarData = evolutionLogs.length > 0 ? (() => {
    const techAvg = ((latest.techServe || 5) + (latest.techForehand || 5) + (latest.techBackhand || 5) + (latest.techReturn || 5) + (latest.techNetPlay || 5)) / 5;
    const tactAvg = ((latest.tactRally || 5) + (latest.tactSelection || 5) + (latest.tactPositioning || 5) + (latest.tactAdaptation || 5)) / 4;
    const physAvg = ((latest.physFootwork || 5) + (latest.physStamina || 5)) / 2;
    const mentalAvg = ((latest.mentalFocus || 5) + (latest.mentalClutch || 5)) / 2;
    const cogAvg = ((latest.cogGameReading || 5) + (latest.cogReaction || 5) + (latest.cogDecision || 5)) / 3;
    
    return [
      { subject: 'Técnico', A: Number(techAvg.toFixed(1)), fullMark: 10 },
      { subject: 'Tático', A: Number(tactAvg.toFixed(1)), fullMark: 10 },
      { subject: 'Físico', A: Number(physAvg.toFixed(1)), fullMark: 10 },
      { subject: 'Mental', A: Number(mentalAvg.toFixed(1)), fullMark: 10 },
      { subject: 'Cognitivo', A: Number(cogAvg.toFixed(1)), fullMark: 10 }
    ];
  })() : [];

  // Evolution Line Data
  const lineData = [...evolutionLogs].reverse().map(log => {
    const tAvg = ((log.techServe || 5) + (log.techForehand || 5) + (log.techBackhand || 5) + (log.techReturn || 5) + (log.techNetPlay || 5)) / 5;
    const taAvg = ((log.tactRally || 5) + (log.tactSelection || 5) + (log.tactPositioning || 5) + (log.tactAdaptation || 5)) / 4;
    const pAvg = ((log.physFootwork || 5) + (log.physStamina || 5)) / 2;
    const mAvg = ((log.mentalFocus || 5) + (log.mentalClutch || 5)) / 2;
    const cAvg = ((log.cogGameReading || 5) + (log.cogReaction || 5) + (log.cogDecision || 5)) / 3;
    return {
      name: new Date(log.date || log.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }),
      Geral: Number(((tAvg + taAvg + pAvg + mAvg + cAvg) / 5).toFixed(1))
    };
  });

  const pageStyle = {
    width: '794px',
    minHeight: '1123px',
    padding: '40px',
    background: 'white',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    color: '#111827',
    boxSizing: 'border-box' as const,
    marginBottom: '20px'
  };

  const ReportHeader = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #E5E7EB', paddingBottom: '20px', marginBottom: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
        <img src={techTennisLogo} alt="TechTennis" style={{ maxHeight: '70px', maxWidth: '180px', objectFit: 'contain' }} />
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', color: '#1E3A8A' }}>Relatório Executivo de Evolução</h1>
          <h2 style={{ margin: '5px 0 0 0', fontSize: '16px', color: '#6B7280' }}>Professor: {professorName}</h2>
        </div>
      </div>
      {professorLogo && <img src={professorLogo} alt="Logo Professor" style={{ maxHeight: '100px', maxWidth: '250px', objectFit: 'contain' }} />}
    </div>
  );

  return (
    <div ref={ref} style={{ position: 'absolute', left: '-9999px', top: '-9999px', display: 'flex', flexDirection: 'column' }}>
      
      {(sections.cadastro || sections.anamnese) && (
        <div className="pdf-page" style={pageStyle}>
          <ReportHeader />

        {/* Cadastro */}
        {sections.cadastro && (
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ fontSize: '18px', color: '#1E3A8A', borderBottom: '1px solid #E5E7EB', paddingBottom: '5px', marginBottom: '15px' }}>Dados do Aluno</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
              <div><strong>Nome:</strong> {student.name}</div>
              <div><strong>Telefone:</strong> {student.phone || 'N/A'}</div>
              <div><strong>Nível:</strong> {student.level || 'Não avaliado'}</div>
              <div><strong>Local:</strong> {student.isResidential ? (student.condoName || 'Condomínio') : 'Clube / Academia'}</div>
              <div><strong>Valor Hora/Aula:</strong> {student.hourlyRate ? `R$ ${Number(student.hourlyRate).toFixed(2)}` : 'N/A'}</div>
            </div>
          </div>
        )}

        {/* Anamnese */}
        {sections.anamnese && student.anamnesis && student.anamnesis.answers && (
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ fontSize: '18px', color: '#1E3A8A', borderBottom: '1px solid #E5E7EB', paddingBottom: '5px', marginBottom: '15px' }}>
              Anamnese e Objetivos ({student.anamnesis.type === 'child' ? 'Criança' : 'Adulto'})
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '12px' }}>
              {Object.entries(student.anamnesis.answers).map(([key, value]) => {
                const labels: any = {
                  handedness: 'Lateralidade', grip: 'Empunhadura', backhand: 'Backhand', experienceLevel: 'Experiência Atual', practiceTime: 'Tempo de prática', otherSports: 'Outros esportes', frequency: 'Frequência pretendida', physicalActivity: 'Pratica ativ. física?', physicalActivityDetails: 'Qual ativ. física', hasInjuries: 'Possui lesões?', injuryAreas: 'Áreas das lesões', injuryObservations: 'Obs sobre lesões', medicalConditions: 'Condições Clínicas', medications: 'Medicamentos', racketModel: 'Modelo Raquete', strings: 'Encordoamento', stringFrequency: 'Troca de cordas', shoes: 'Calçado específico?', motivation: 'Motivação Principal', expectations: 'Expectativas com prof.', mobility: 'Mobilidade', strengths: 'Pontos Fortes', adjustments: 'Ajustes Prioritários', plan: 'Plano de Desenvolvimento', practiceBefore: 'Já praticou tênis?', sportsHandling: 'Comportamento esportivo', otherActivities: 'Outras atividades', otherActivitiesDetails: 'Detalhes Outras', healthConditions: 'Condição de saúde', healthConditionsDetails: 'Detalhes Saúde', glasses: 'Usa Óculos/Aparelho', racket: 'Possui raquete?', parentExpectations: 'Expectativa dos Pais', coordination: 'Coordenação Motora', attention: 'Atenção e Foco', engagement: 'Engajamento'
                };
                if (!value || (Array.isArray(value) && value.length === 0)) return null;
                const label = labels[key] || key;
                const displayValue = Array.isArray(value) ? value.join(', ') : String(value);
                return (
                  <div key={key}><strong>{label}:</strong> {displayValue}</div>
                );
              })}
            </div>
          </div>
        )}
        {sections.anamnese && (!student.anamnesis || !student.anamnesis.answers) && (
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ fontSize: '18px', color: '#1E3A8A', borderBottom: '1px solid #E5E7EB', paddingBottom: '5px', marginBottom: '15px' }}>Anamnese e Objetivos</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '14px' }}>
              <div><strong>Histórico Médico / Lesões:</strong><br/>{student?.anamnesis?.medicalHistory || 'Nenhum histórico informado.'}</div>
              <div><strong>Objetivos de Curto Prazo:</strong><br/>{student?.anamnesis?.shortTermGoals || 'Não informado.'}</div>
              <div><strong>Objetivos de Longo Prazo:</strong><br/>{student?.anamnesis?.longTermGoals || 'Não informado.'}</div>
            </div>
          </div>
        )}

        </div>
      )}

      {/* Desempenho */}
      {sections.desempenho && evolutionLogs.length > 0 && (
        <div className="pdf-page" style={pageStyle}>
          <ReportHeader />
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ fontSize: '18px', color: '#1E3A8A', borderBottom: '1px solid #E5E7EB', paddingBottom: '5px', marginBottom: '15px' }}>Avaliação de Desempenho ({student.level || 'S/ Nível'})</h3>
            
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ flex: 1, height: '250px', background: '#F9FAFB', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
                    <PolarGrid stroke="#E5E7EB" />
                    <PolarAngleAxis dataKey="subject" tick={{ fill: '#4B5563', fontSize: 10, fontWeight: 600 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 10]} tick={false} axisLine={false} />
                    <Radar name="Atual" dataKey="A" stroke="#10B981" fill="#10B981" fillOpacity={0.4} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ flex: 1, height: '250px', background: '#F9FAFB', borderRadius: '12px', padding: '10px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={lineData} margin={{ top: 20, right: 30, left: -20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                    <XAxis dataKey="name" tick={{ fill: '#6B7280', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis domain={[0, 10]} tick={{ fill: '#6B7280', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} />
                    <Line type="monotone" dataKey="Geral" stroke="#3B82F6" strokeWidth={3} dot={{ r: 4, fill: '#3B82F6', strokeWidth: 0 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Sub-notas detalhadas */}
            {(() => {
              const kpis = [
                { category: 'Técnico', color: '#3B82F6', items: [
                    { name: 'Saque', value: latest.techServe || 0 },
                    { name: 'Forehand', value: latest.techForehand || 0 },
                    { name: 'Backhand', value: latest.techBackhand || 0 },
                    { name: 'Devolução', value: latest.techReturn || 0 },
                    { name: 'Jogo de Rede', value: latest.techNetPlay || 0 }
                ]},
                { category: 'Tático', color: '#10B981', items: [
                    { name: 'Consistência de Troca', value: latest.tactRally || 0 },
                    { name: 'Seleção de Golpes', value: latest.tactSelection || 0 },
                    { name: 'Posicionamento', value: latest.tactPositioning || 0 },
                    { name: 'Adaptação ao Oponente', value: latest.tactAdaptation || 0 }
                ]},
                { category: 'Físico', color: '#F59E0B', items: [
                    { name: 'Movimentação/Footwork', value: latest.physFootwork || 0 },
                    { name: 'Resistência/Stamina', value: latest.physStamina || 0 }
                ]},
                { category: 'Mental', color: '#8B5CF6', items: [
                    { name: 'Foco/Concentração', value: latest.mentalFocus || 0 },
                    { name: 'Controle Emocional (Clutch)', value: latest.mentalClutch || 0 }
                ]},
                { category: 'Cognitivo', color: '#EC4899', items: [
                    { name: 'Leitura de Jogo e Antecipação', value: latest.cogGameReading || 0 },
                    { name: 'Tempo de Reação', value: latest.cogReaction || 0 },
                    { name: 'Tomada de Decisão (Pressão)', value: latest.cogDecision || 0 }
                ]}
              ];

              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                  {kpis.map(cat => (
                    <div key={cat.category}>
                      <h5 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#4B5563', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: cat.color }} />
                        {cat.category}
                      </h5>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {cat.items.map(item => (
                          <div key={item.name}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                              <span style={{ color: '#374151', fontWeight: 500 }}>{item.name}</span>
                              <span style={{ color: cat.color, fontWeight: 700 }}>
                                {item.value} / 10 {student.level && <span style={{ fontSize: '9px', fontWeight: 'normal', color: '#6B7280', marginLeft: '4px' }}>({student.level})</span>}
                              </span>
                            </div>
                            <div style={{ height: '4px', background: '#F3F4F6', borderRadius: '4px', overflow: 'hidden' }}>
                              <div style={{ height: '100%', background: cat.color, width: `${(item.value / 10) * 100}%`, borderRadius: '4px' }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {evolutionLogs.filter((l: any) => l.generalFeedback).length > 0 && (
              <div style={{ background: '#F3F4F6', padding: '15px', borderRadius: '8px' }}>
                <strong style={{ fontSize: '16px', color: '#1E3A8A', display: 'block', marginBottom: '10px' }}>Histórico de Feedbacks:</strong>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {evolutionLogs.filter((l: any) => l.generalFeedback).slice(0, 5).map((log: any, idx: number) => (
                    <div key={idx} style={{ background: 'white', padding: '10px', borderRadius: '6px', borderLeft: '4px solid #3B82F6', pageBreakInside: 'avoid' }}>
                      <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>
                        {new Date(log.date || log.createdAt).toLocaleDateString('pt-BR')}
                      </div>
                      <p style={{ margin: 0, fontSize: '13px', fontStyle: 'italic', color: '#374151' }}>
                        "{log.generalFeedback}"
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

        </div>
      )}

      {/* Financeiro */}
      {sections.financeiro && finances.length > 0 && (
        <div className="pdf-page" style={pageStyle}>
          <ReportHeader />
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ fontSize: '18px', color: '#1E3A8A', borderBottom: '1px solid #E5E7EB', paddingBottom: '5px', marginBottom: '15px' }}>Resumo Financeiro</h3>
            
            {(() => {
              const getWeeklyHours = (studentClasses: any[]) => {
                if (!studentClasses || studentClasses.length === 0) return 1;
                const weeklySums: Record<string, number> = {};
                studentClasses.forEach(c => {
                  const d = new Date(c.date + 'T00:00:00');
                  const firstDay = new Date(d.getFullYear(), 0, 1);
                  const pastDays = (d.getTime() - firstDay.getTime()) / 86400000;
                  const weekNum = Math.ceil((pastDays + firstDay.getDay() + 1) / 7);
                  weeklySums[weekNum] = (weeklySums[weekNum] || 0) + c.duration;
                });
                return Math.max(...Object.values(weeklySums)) || 1;
              };

              let totalExpected = 0;
              let realizedHours = 0;
              const plannedHours = finances.reduce((acc: number, c: any) => acc + c.duration, 0);

              if (student.pricingMode === 'fixed') {
                if (student.fixedMonthlyHours) {
                  totalExpected = Number(student.fixedMonthlyHours) * (student.hourlyRate || 0);
                } else {
                  totalExpected = 4 * getWeeklyHours(finances) * (student.hourlyRate || 0);
                }
                realizedHours = finances.filter((c: any) => c.status === 'completed' || c.status === 'replacement' || c.status === 'Realizada' || c.status === 'Reposição').reduce((acc: number, c: any) => acc + c.duration, 0);
              } else if (student.pricingMode === 'planned') {
                totalExpected = plannedHours * (student.hourlyRate || 0);
                realizedHours = finances.filter((c: any) => c.status === 'completed' || c.status === 'replacement' || c.status === 'Realizada' || c.status === 'Reposição').reduce((acc: number, c: any) => acc + c.duration, 0);
              } else {
                realizedHours = finances.filter((c: any) => c.status === 'completed' || c.status === 'replacement' || c.status === 'Realizada' || c.status === 'Reposição').reduce((acc: number, c: any) => acc + c.duration, 0);
                totalExpected = realizedHours * (student.hourlyRate || 0);
              }

              const baseData = allFinances || finances;
              const pendingReplacement = baseData.filter((c: any) => 
                c.status === 'cancelled_prof' || 
                c.status === 'cancelled_rain' ||
                ((c.status === 'cancelled' || c.status === 'Cancelada' || c.status === 'canceled' || c.status === 'cancelled_student') && c.willHaveReplacement)
              ).reduce((acc: number, c: any) => acc + c.duration, 0);
              
              const doneReplacement = baseData.filter((c: any) => c.status === 'replacement' || c.status === 'Reposição').reduce((acc: number, c: any) => acc + c.duration, 0);
              const finalPendingReplacement = Math.max(0, pendingReplacement - doneReplacement);

              const labelTotal = student.pricingMode === 'fixed' ? '(Fixo/Mês)' : student.pricingMode === 'planned' ? '(Aulas Previstas)' : '(Aulas Realizadas)';
              const labelHoras = student.pricingMode === 'planned' ? 'Horas Previstas' : 'Horas Realizadas';
              const valueHoras = student.pricingMode === 'planned' ? plannedHours.toFixed(1) : realizedHours.toFixed(1);

              return (
                <div style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
                  <div style={{ flex: 1, padding: '16px', background: '#EFF6FF', borderRadius: '12px', border: '1px solid #BFDBFE' }}>
                    <div style={{ fontSize: '12px', color: '#1E40AF', marginBottom: '8px', fontWeight: 600 }}>Total Financeiro {labelTotal}</div>
                    <div style={{ fontSize: '24px', color: '#1E3A8A', fontWeight: 700 }}>R$ {totalExpected.toFixed(2)}</div>
                  </div>
                  <div style={{ flex: 1, padding: '16px', background: '#F0FDF4', borderRadius: '12px', border: '1px solid #BBF7D0' }}>
                    <div style={{ fontSize: '12px', color: '#166534', marginBottom: '8px', fontWeight: 600 }}>{labelHoras}</div>
                    <div style={{ fontSize: '24px', color: '#14532D', fontWeight: 700 }}>{valueHoras}h</div>
                  </div>
                  <div style={{ flex: 1, padding: '16px', background: '#FEF2F2', borderRadius: '12px', border: '1px solid #FECACA' }}>
                    <div style={{ fontSize: '12px', color: '#991B1B', marginBottom: '8px', fontWeight: 600 }}>Horas P/ Reposição</div>
                    <div style={{ fontSize: '24px', color: '#7F1D1D', fontWeight: 700 }}>{finalPendingReplacement.toFixed(1)}h</div>
                  </div>
                </div>
              );
            })()}

            <h3 style={{ fontSize: '18px', color: '#1E3A8A', borderBottom: '1px solid #E5E7EB', paddingBottom: '5px', marginBottom: '15px' }}>Extrato Detalhado (Aulas)</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #E5E7EB', color: '#6B7280', textAlign: 'left' }}>
                  <th style={{ padding: '8px 0' }}>DATA</th>
                  <th style={{ padding: '8px 0' }}>ALUNO</th>
                  <th style={{ padding: '8px 0' }}>STATUS</th>
                  <th style={{ padding: '8px 0' }}>DURAÇÃO</th>
                  <th style={{ padding: '8px 0', textAlign: 'right' }}>VALOR A COBRAR</th>
                </tr>
              </thead>
              <tbody>
                {finances.map((fin: any, idx: number) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6', color: '#374151' }}>
                    <td style={{ padding: '12px 0', fontWeight: 500 }}>{new Date(fin.date).toLocaleDateString('pt-BR', {timeZone: 'UTC'})}</td>
                    <td style={{ padding: '12px 0' }}>{student.name}</td>
                    <td style={{ padding: '12px 0' }}>{fin.status}</td>
                    <td style={{ padding: '12px 0' }}>{fin.duration}h</td>
                    <td style={{ padding: '12px 0', textAlign: 'right', fontWeight: 600 }}>R$ {Number(fin.value).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: '40px', textAlign: 'center', fontSize: '12px', color: '#9CA3AF' }}>
            Gerado pelo Aplicativo TechTennis em {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR')}
          </div>
        </div>
      )}
    </div>
  );
});
